/// <reference types="vite/client" />

import type { AxiosInstance } from "axios";
import { http } from "@/lib/http";

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
}

export class ApiService {
  private instance: AxiosInstance;

  constructor() {
    this.instance = http.client;
  }

  // Generic request methods
  async get<T>(url: string): Promise<ApiResponse<T>> {
    const response = await this.instance.get<ApiResponse<T>>(url);
    return response.data;
  }

  async post<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response = await this.instance.post<ApiResponse<T>>(url, data);
    return response.data;
  }

  async put<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response = await this.instance.put<ApiResponse<T>>(url, data);
    return response.data;
  }

  async delete<T>(url: string): Promise<ApiResponse<T>> {
    const response = await this.instance.delete<ApiResponse<T>>(url);
    return response.data;
  }

  async patch<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response = await this.instance.patch<ApiResponse<T>>(url, data);
    return response.data;
  }

  // ML-specific methods
  async checkStatus(): Promise<ApiResponse<{ status: string }>> {
    return this.get("/mlx/status");
  }

  async runInference(
    data: number[],
    options: {
      model: string;
      batch_size?: number;
      precision?: "float32" | "float16";
    },
  ): Promise<ApiResponse<number[]>> {
    return this.post("/mlx/inference", { data, ...options });
  }

  async getModels(): Promise<ApiResponse<string[]>> {
    return this.get("/mlx/models");
  }

  async loadModel(modelName: string): Promise<ApiResponse<void>> {
    return this.post(`/mlx/models/${modelName}/load`);
  }

  async unloadModel(modelName: string): Promise<ApiResponse<void>> {
    return this.delete(`/mlx/models/${modelName}`);
  }
}

export const api = new ApiService();
export const apiClient = api.instance;
